import access from '../config/moduleAccess.json';

export const GYM_MODULES = {
  invoice: 'invoice',
  trainerStaff: 'staff',
  slotBooking: 'slotBooking',
  memberRetention: 'retention',
};

export function getId(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value._id || value.id || '';
}

export function getCurrentGym(user, gyms = []) {
  const gymId = getId(user?.gymId);
  if (!gymId) return null;
  return gyms.find(gym => getId(gym) === gymId) || null;
}

export function hasGymModule(gym, moduleKey) {
  if (!moduleKey) return true;
  if (!gym || gym.isActive === false) return false;
  const pricing = gym.onboardingPricing;
  const key = Object.keys(access.legacyAliases).find(key => access.legacyAliases[key] === moduleKey) || moduleKey;
  if (pricing?.catalogVersion === 2) {
    if (!access.tiers.includes(pricing.tierId)) return false;
    if (access.core.includes(key)) return true;
    return access.addons.includes(key) && pricing.features?.some(feature => feature.key === key && feature.selected === true);
  }
  const features = gym?.onboardingPricing?.features;
  if (access.core.includes(key) && !['invoice', 'slotBooking'].includes(key)) return true;
  if (!Array.isArray(features)) return false;
  return features.some(feature => [key, access.legacyAliases[key]].filter(Boolean).includes(feature.key) && feature.selected === true);
}

const admin = ['superadmin', 'gym_admin', 'admin'];
const training = [...admin, 'trainer', 'coach', 'manager'];
const sales = [...admin, 'manager', 'sales_staff'];
const operations = [...admin, 'manager', 'front_desk'];
// The sidebar and direct URL guard use this same policy.
export const GYM_ROUTES = {
  users: { module: 'userManagement', roles: [...training, 'sales_staff', 'front_desk'] },
  batches: { module: 'batchManagement', roles: training },
  staff: { module: 'staff', roles: admin },
  trainers: { module: 'staff', roles: training },
  marketing: { module: 'communications', roles: sales },
  notifications: { module: 'communications', roles: sales },
  'notification-templates': { module: 'communications', roles: admin },
  banners: { module: 'retention', roles: [...admin, 'manager'] },
  'challenges-events': { module: 'retention', roles: admin },
  retention: { module: 'retention', roles: sales },
  feedback: { module: 'retention', roles: admin },
  leads: { module: 'crm', roles: sales },
  'social-operations': { module: 'social', roles: sales },
  exercises: { module: 'member_app', roles: training },
  'workout-plans': { module: 'member_app', roles: training },
  'meal-plans': { module: 'member_app', roles: training },
  'food-items': { module: 'member_app', roles: training },
  'trainer-plans': { module: 'member_app', roles: training },
  goals: { module: 'member_app', roles: training },
  attendance: { module: 'attendance', roles: operations },
  'slot-management': { module: 'slotBooking', roles: operations },
  pos: { module: 'pos', roles: [...operations, 'sales_staff'] },
  payments: { module: 'invoice', roles: sales },
  'gym-upi-payments': { module: 'invoice', roles: sales },
  invoices: { module: 'invoice', roles: sales },
  'invoice-settings': { module: 'invoice', roles: sales },
  biometric: { module: 'biometric', roles: admin },
  integrations: { module: 'biometric', roles: admin },
  'gym-settings': { roles: operations },
  'yoga-content': { module: 'wellbeing', roles: admin },
  'stress-content': { module: 'wellbeing', roles: admin },
  database: { roles: ['superadmin'] },
  'gym-invoices': { roles: ['superadmin'] },
  audit: { roles: ['superadmin', 'admin'] },
};

export function canAccessGymRoute(user, gyms, pathname) {
  if (!user) return false;
  if (user.role === 'superadmin') return true;
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'dashboard') return false;
  if (!segments[1]) return true;
  if (segments[1] === 'gyms') return user.role === 'gym_admin' && segments[2] === getId(user.gymId);
  const rule = segments[1] === 'users' && ['analytics', 'measurements'].includes(segments[3])
    ? { module: 'member_app', roles: training } : GYM_ROUTES[segments[1]];
  return Boolean(rule && rule.roles.includes(user.role) && userCanAccessGymModule(user, gyms, rule.module));
}

export function userCanAccessGymModule(user, gyms, moduleKey) {
  if (!moduleKey) return true;
  if (user?.role === 'superadmin') return true;
  return hasGymModule(getCurrentGym(user, gyms), moduleKey);
}
